/* eslint-disable max-lines */
import {
  Component, OnInit, Inject, Input, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef, ViewEncapsulation
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subscription, zip, combineLatest } from 'rxjs';
import { map, tap, take } from 'rxjs/operators';
import { Meta, MetaDefinition } from '@angular/platform-browser';

import { environment as ENV } from '@environment';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { HomeFacade } from '@home/home.facade';
import { HomeDataResponse } from '@shared/home-sections/entities/home-data.response';
import { AppFacade } from '@app/app.facade';
import { EventDataLayer } from '@avo/interface/trigger-props';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchType } from '@search-bar/entities/search-type';
import { MODULES_ROUTING } from '@app/modules/core/mappers/modules.mapper';
import { ModuleEntity, ModuleEntityGroup } from '@app/entities/modules-country';
import { UserInfo, UserLoyaltyType } from '@app/modules/core/interfaces/user';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { AvoService } from '@avo/services/avo.service';
import { LoginType } from '@app/entities/login-type';
import { userHistoryRoute } from '@user-history/constants/history-routes.constants';
import { AbandonedReservationFacade } from '@abandoned-reservation/store/abandoned-reservation.facade';
import { RecentSearchesFacade } from '@recent-searches/store/recent-searches.facade';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { countryModulesByWidgetsConfig } from '@home/mappers/home.mapper';
import {
  TypographyFontSize, TypographyFontFamily, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-home',
  templateUrl: './home.container.html',
  styleUrls: ['./home.container.sass'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class HomeContainer implements OnInit, OnDestroy {

  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.TRAVEL_HOME;

  @ViewChild('modalWebView') modalWebView: ElementRef;

  @ViewChild('homeContainer') homeContainer: ElementRef;

  public heightGalleryDestination: number = 400;
  public heightGallery: number = 400;
  public headerAnimation: boolean = false;
  public showOperatiorCert: boolean = false;
  public footerOperatedByImages: string = null;
  public footerDynamic: string = '';

  public readonly fontSize = TypographyFontSize;
  public readonly fontFamily = TypographyFontFamily;
  public readonly fontColor = TypographyColor;

  private subscriptions: Subscription = new Subscription();
  private scrollTopAnimation: number = 190;
  private repentanceUrl: string = ENV.repentance_link;
  public loader$: Observable<boolean>;
  public loader2$: Observable<boolean>;
  public loader3$: Observable<boolean>;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly facade: HomeFacade,
    private readonly appFacade: AppFacade,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    private readonly avoService: AvoService,
    private readonly metaService: Meta,
    private readonly abandonedReservationFacade: AbandonedReservationFacade,
    private readonly recentSearchesFacade: RecentSearchesFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {}

  @HostListener('window:scroll', ['$event']) onWindowScroll(event: Event): void {
    this.isMobile$.subscribe(isMobile => {
      if (isMobile) {
        const offset = window['pageYOffset'] || document.documentElement.scrollTop || document.body.scrollTop || 0;
        this.headerAnimation = offset >= this.scrollTopAnimation;
        this.heightGallery = (this.heightGalleryDestination - offset) > this.heightGalleryDestination ?
          this.heightGalleryDestination : (this.heightGalleryDestination - offset);
        this.changeDetector.detectChanges();
      }
    }).unsubscribe();
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.appFacade.historyReset();

      combineLatest([
        this.appFacade.isLoggedIn$,
        this.isMobile$.pipe(take(1)),
      ]).subscribe(([isLoggedIn, isMobile]) => {
        if (isLoggedIn) {
          this.registerAmplitudeEvent();
          this.recentSearchesFacade.fetchRecentSearches();
          this.abandonedReservationFacade.fetchAbandonedReservation();
          if (isMobile) {
            this.facade.fetchUserHistoryListByHome();
          }
        }
      }).unsubscribe();
      this.resetParams();
      this.baseSearchBarFacade.fetchDefaultPlace(SearchType.HOME_FLIGHT);
      this.subscriptions.add(
        this.homeData$.subscribe(homeData => {
          this.writeDataSEO(homeData?.metas, homeData?.footer_content);
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get user$(): Observable<UserInfo> {
    return this.appFacade.user$;
  }

  get homeData$(): Observable<HomeDataResponse> {
    return this.facade.homeData$;
  }

  get isLoadingHome$(): Observable<boolean> {
    return this.facade.isLoadingHome$;
  }
  
  get showMobileHeader$(): Observable<boolean> {
    return combineLatest([this.isMobile$, this.isLoadingHome$]).pipe(map(([isMobile, isLoadingHome]) =>
      isMobile && !isLoadingHome));
  }

  get isLoadingHistory$(): Observable<boolean> {
    return this.facade.isLoadingHistory$;
  }

  get toggleUserHistory$(): Observable<boolean> {
    return combineLatest([this.isLoggedIn$, this.isLoadingHistory$]).pipe(map(([isLoggedIn, isLoadingHistory]) =>
      isLoggedIn && !isLoadingHistory));
  }

  get showBtnAlerts$(): Observable<boolean> {
    return combineLatest([this.loading$, this.isLoggedIn$]).pipe(map(([loading, isLoggedIn]) => isLoggedIn && !loading));
  }

  get loading$(): Observable<boolean> {
    return zip(this.isLoadingHome$, this.isLoadingHistory$).pipe(
      map(([isLoadingHome, isLoadingHistory]) => (isLoadingHome || isLoadingHistory)));
  }

  get showSupport$(): Observable<boolean> {
    return zip(
      this.isLoggedIn$,
      this.user$
    ).pipe(
      map(([isLoggedIn, user]) => isLoggedIn &&
        (user?.has_rappicard || user?.loyalty?.type === UserLoyaltyType.Diamond || user?.is_prime))
    );
  }

  get countryModulesCollection$(): Observable<ModuleEntityGroup[]> {
    return this.homeData$.pipe(
      map(homeData => countryModulesByWidgetsConfig(homeData?.widgets, true) as ModuleEntityGroup[])
    );
  }

  get routeSearch$(): Observable<string[]> {
    return this.homeData$.pipe(
      map((homeData) => {
        const {widgets} = homeData;
        return MODULES_ROUTING[widgets?.split(',')[0]]
      })
    )
  }

  public chipActionEvent(module: ModuleEntity): void {
    this.router.navigate(module.route, { queryParams: { source: this.source } });
  }

  public trackEvent(event: EventDataLayer): void {
    this.avoService.getEventTriggerProps$(event.eventType, { ...event.eventProperties, SOURCE: this.source })
      .subscribe(trigger => {
        if (event.eventType === AVO_EVENTS.SELECT_BANNER) {
          this.AvoDispatch.selectBanner({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
            titleSection: trigger?.EVENT_PROPS?.TITLE_SECTION as string,
            titlePromo: trigger?.EVENT_PROPS?.TITLE_PROMO as string,
            indexPromo: trigger?.EVENT_PROPS?.INDEX_PROMO as number,
            indexSection: trigger?.EVENT_PROPS?.INDEX_SECTION as number,
            typeWidget: trigger?.EVENT_PROPS?.TYPE_WIDGET as string,
          })
        } else {
          triggerAmplitudeEventApp(trigger);
        }
      }).unsubscribe();
  }

  public itemTrackBy(index: number, item: ModuleEntity): string {
    return item.name;
  }

  public onMyFlights(): void {
    combineLatest([this.isLoggedIn$, this.isLoadingHistory$, this.user$]).subscribe(
      ([isLoggedIn, isLoadingHistory, user]) => {
        if (isLoggedIn && !isLoadingHistory && user) {
          this.router.navigate([`${userHistoryRoute}`, user?.id]);
        } else {
          this.headerService.forceUserLogin(LoginType.MyFlights)
        }
      }).unsubscribe();
  }

  private registerAmplitudeEvent(): void {
    this.avoService.getEventTriggerProps$(AVO_EVENTS.VIEW_TRAVEL_HOME, {
      SOURCE: this.route.snapshot.queryParamMap.get(SOURCE_PARAM) || EVENT_SOURCE.TRAVEL_HOME,
      PAGE_CODE: ENV.localeCMS, BRANCH_CODE: this.route.snapshot.queryParamMap.get('branch_code') || ENV.branchcode
    }).pipe(tap(trigger => {
      this.viewTravelHomeByAvo(trigger);
    })).subscribe().unsubscribe();
  }

  private resetParams(): void {
    this.facade.resetWidgetData(); this.facade.resetSalesChannel();
    this.facade.resetAirline(); this.facade.disabledFlightScroll();
    this.baseSearchBarFacade.resetPackageSelectedOptions(); this.baseSearchBarFacade.resetPackageStep();
    this.facade.resetSalesChannel(); this.facade.resetWidgetData();
  }

  private writeDataSEO(metas?: MetaDefinition[], footer_content?: string): void {
    if (metas) {
      metas.map(meta => this.metaService.removeTag(`name='${meta.name}'`));
      this.metaService.addTags(metas);
    }
    if (footer_content) {
      this.footerDynamic = footer_content;
    }
  }

  private viewTravelHomeByAvo(trigger: TriggerEvent): void {
    this.AvoDispatch.viewTravelHome({
      ...this.avoService.avoBaseProperties(trigger),
      source: this.avoService.getAvoSourceValueType(trigger.EVENT_PROPS?.SOURCE),
      pageCode: trigger.EVENT_PROPS?.PAGE_CODE as string,
      branchCode: trigger.EVENT_PROPS?.BRANCH_CODE as string
    });
  }
}
